import * as THREE from 'three'
import { ProceduralCity } from './ProceduralCity'
import { domeCities,type DomeWorldId } from './domeWorlds'

export class DomeArchitecture extends ProceduralCity {
  protected cap=new THREE.SphereGeometry(1,32,20,0,Math.PI*2,0,Math.PI/2)
  protected ring=new THREE.TorusGeometry(1,.045,8,48)
  protected rock=new THREE.IcosahedronGeometry(1,1)
  protected point=new THREE.ConeGeometry(1,1,24)
  protected palette
  constructor(readonly city:DomeWorldId){super();this.palette=domeCities[city];[this.cap,this.ring,this.rock,this.point].forEach(g=>this.geometries.add(g))}
  protected hoop(x:number,y:number,z:number,r:number,color:string){
    const wide=r>15,geometry=wide?new THREE.TorusGeometry(r,.18,8,96):this.ring
    if(wide)this.geometries.add(geometry)
    this.add(geometry,this.mat(color,.4),x,y,z,wide?1:r,wide?1:r,wide?1:r,0,new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1,0,0),Math.PI/2))
  }
  protected shell(x:number,y:number,z:number,r:number,h:number,color:string=this.palette.glass){this.add(this.cap,this.mat(color,.32),x,y,z,r,h,r);this.hoop(x,y,z,r,this.palette.trim)}
  protected windows(x:number,z:number,w:number,d:number,h:number,y=.4){
    const p=this.palette
    this.box(x,y+h/2,z,w,h,d,p.wall)
    for(let side=0;side<4;side++)this.at(x,z,1,side*Math.PI/2,()=>{
      const width=side%2?d:w,depth=side%2?w:d
      for(let yy=y+.8;yy<y+h-.3;yy+=1.15){
        this.box(0,yy,depth/2+.025,width*.84,.48,.055,this.mat(p.glass,.4,.06))
        for(let xx=-width*.38;xx<width*.4;xx+=.62)this.box(xx,yy,depth/2+.065,.045,.52,.065,p.trim)
      }
      this.box(0,y+h,depth/2+.07,width+.16,.14,.18,p.trim)
    })
    if(this.city==='atlantis')for(const s of [-1,1])for(const t of [-1,1])this.disc(x+s*(w/2-.12),y+h/2,z+t*(d/2+.08),.11,h,p.trim)
    else for(const s of [-1,1])this.box(x+s*(w/2+.05),y+h/2,z,.11,h,d+.08,p.trim)
  }
  protected entrance(x:number,z:number,w=1,y=.4){const p=this.palette;this.box(x,y+.65,z,w,1.3,.1,p.trim);this.box(x,y+.65,z+.07,w*.7,1.06,.08,p.glass);this.box(x,y+1.39,z+.15,w+.3,.12,.4,p.accent);this.box(x,y+.09,z+.25,w+.35,.16,.65,p.wall)}
  protected solar(x:number,y:number,z:number,w:number,d:number){
    this.box(x,y-.2,z,.12,.5,.12,this.palette.trim);this.box(x,y,z,w,.1,d,'#284962');for(let xx=-w/2+.15;xx<w/2;xx+=.45)this.box(x+xx,y+.055,z,.025,.015,d,'#7daab5');for(let zz=-d/2+.1;zz<d/2;zz+=.5)this.box(x,y+.055,z+zz,w,.015,.025,'#7daab5')
  }
  protected garden(x:number,z:number,scale=1){this.at(x,z,scale,0,()=>{
    this.disc(0,.35,0,.8,.5,this.palette.trim)
    for(let i=0;i<5;i++){const a=i*2.4;this.beam(new THREE.Vector3(0,.5,0),new THREE.Vector3(Math.cos(a)*.42,1.1+i*.16,Math.sin(a)*.42),.04,'#638e78');this.ball(Math.cos(a)*.42,1.1+i*.16,Math.sin(a)*.42,.24,this.city==='atlantis'?['#c58692','#86bca1','#8d9ec5'][i%3]:'#80ac7c',.7)}
  })}
  protected lamp(x:number,z:number){this.disc(x,1.6,z,.07,3,this.palette.trim);this.ball(x,3.13,z,.18,this.mat(this.palette.light,.1,.65));this.hoop(x,3.05,z,.3,this.palette.accent)}
  protected terrace(){this.box(0,.18,0,24,.35,24,this.palette.base);for(const s of [-1,1]){this.box(s*11.4,.38,0,.1,.05,23,this.palette.trim);this.box(0,.38,s*11.4,23,.05,.1,this.palette.trim)}}
  protected rover(index:number){
    const g=new THREE.Group(),p=this.palette,ocean=this.city==='atlantis',color=index%4===0?p.accent:p.wall
    this.box(0,.48,0,.75,.35,1.45,color);this.box(0,.78,-.18,.65,.42,.75,p.glass);this.box(0,1,-.18,.73,.07,.85,color)
    this.box(0,.48,.75,.6,.1,.06,this.mat(p.light,0,.55));this.box(0,.4,-.76,.58,.06,.06,'#dc795b')
    for(const s of [-1,1])for(const z of [-.53,0,.53])this.add(this.cylinder,this.mat('#3e4c59'),s*.43,.29,z,.22,.14,.22,0,new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0,0,1),Math.PI/2))
    if(!ocean){this.disc(.24,1.23,-.3,.025,.55,p.trim);this.ball(.24,1.54,-.3,.065,p.light);this.solar(0,.77,.4,.63,.4)}
    this.flush(g);return g
  }
  protected citizen(index:number){
    const g=new THREE.Group(),p=this.palette,alien=index%3===0&&this.city!=='atlantis',skin=alien?(this.city==='solara'?'#9cb88c':'#b3bdca'):['#c18e6f','#9b7057','#dfb394'][index%3]
    this.box(0,.62,0,.25,.38,.21,index%2?p.wall:p.accent);this.ball(0,.95,0,alien?.18:.14,skin,alien?1.15:1)
    for(const x of [-.055,.055])this.ball(x,.96,.125,alien?.042:.025,'#27394c',1.3)
    for(const s of [-1,1]){this.box(s*.18,.62,0,.08,.33,.08,p.wall);this.box(s*.075,.28,0,.09,.31,.11,p.trim)}
    if(!alien&&this.city!=='atlantis'){this.box(0,.71,-.14,.22,.25,.13,p.trim);this.hoop(0,.85,0,.17,p.light)}
    this.flush(g);return g
  }
}
